import React from "react";
import dayjs from "dayjs";
import { ProfileRow } from "../ProfileRow";
import { renderValue } from "../renderValueHelper";
import { formatDate } from "../../utils/profileFormatters";
import { useTranslation } from "@helpers/translate";
import changeIcon from "@assets/images/icons/change-icon.svg";
import saveIcon from "@assets/images/icons/save-icon.svg";
import cancelIcon from "@assets/images/icons/cancel-icon.svg";

import "./index.sass";

const EMPTY_DATE = {
  day: "",
  month: "",
  year: "",
};

export const DateOfBirthRow = ({
  date_birth,
  editingField,
  editingValues,
  setEditingValues,
  dayInputRef,
  monthInputRef,
  yearInputRef,
  handleSaveDateOfBirth,
  setEditingField,
  savingField,
  fieldError,
  clearFieldError,
}) => {
  const { __ } = useTranslation();

  const hasDateOfBirth = Boolean(date_birth);

  const currentDate = editingValues.date_birth || EMPTY_DATE;


  const getInitialDateValue = () => {
    if (!date_birth) {
      return EMPTY_DATE;
    }

    const dateObject = dayjs(date_birth);

    if (!dateObject.isValid()) {
      return EMPTY_DATE;
    }

    return {
      day: dateObject.format("DD"),
      month: dateObject.format("MM"),
      year: dateObject.format("YYYY"),
    };
  };

  const cancelEditing = () => {
    setEditingField(null);
    clearFieldError("date_birth");

    setEditingValues((previousValues) => ({
      ...previousValues,
      date_birth: getInitialDateValue(),
    }));
  };

  const handleSave = () => {
    if (hasDateOfBirth) {
      return;
    }

    handleSaveDateOfBirth(currentDate);
  };

  const handleStartEditing = () => {
    if (hasDateOfBirth) {
      return;
    }

    setEditingField("date_birth");

    setEditingValues((previousValues) => ({
      ...previousValues,
      date_birth: EMPTY_DATE,
    }));
  };

  const value =
    editingField === "date_birth" && !hasDateOfBirth ? (
      <div className="profile-row__date-inputs">
        <input
          ref={dayInputRef}
          type="text"
          inputMode="numeric"
          className="profile-row__date-input"
          placeholder="DD"
          maxLength={2}
          value={currentDate.day}
          onChange={(event) => {
            const nextValue = event.target.value.replace(/\D/g, "");

            clearFieldError("date_birth");

            setEditingValues((previousValues) => ({
              ...previousValues,
              date_birth: {
                ...previousValues.date_birth,
                day: nextValue,
              },
            }));

            if (
              nextValue.length === 2 &&
              monthInputRef.current
            ) {
              monthInputRef.current.focus();
            }
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              handleSave();
            }

            if (event.key === "Escape") {
              cancelEditing();
            }
          }}
        />

        <span className="profile-row__date-separator">/</span>

        <input
          ref={monthInputRef}
          type="text"
          inputMode="numeric"
          className="profile-row__date-input"
          placeholder="MM"
          maxLength={2}
          value={currentDate.month}
          onChange={(event) => {
            const nextValue = event.target.value.replace(/\D/g, "");

            clearFieldError("date_birth");

            setEditingValues((previousValues) => ({
              ...previousValues,
              date_birth: {
                ...previousValues.date_birth,
                month: nextValue,
              },
            }));

            if (
              nextValue.length === 2 &&
              yearInputRef.current
            ) {
              yearInputRef.current.focus();
            }
          }}
          onKeyDown={(event) => {
            if (
              event.key === "Backspace" &&
              event.currentTarget.value === "" &&
              dayInputRef.current
            ) {
              dayInputRef.current.focus();
            }

            if (event.key === "Enter") {
              event.preventDefault();
              handleSave();
            }

            if (event.key === "Escape") {
              cancelEditing();
            }
          }}
        />

        <span className="profile-row__date-separator">/</span>

        <input
          ref={yearInputRef}
          type="text"
          inputMode="numeric"
          className="profile-row__date-input"
          placeholder="YYYY"
          maxLength={4}
          value={currentDate.year}
          onChange={(event) => {
            const nextValue = event.target.value.replace(/\D/g, "");

            clearFieldError("date_birth");

            setEditingValues((previousValues) => ({
              ...previousValues,
              date_birth: {
                ...previousValues.date_birth,
                year: nextValue,
              },
            }));
          }}
          onKeyDown={(event) => {
            if (
              event.key === "Backspace" &&
              event.currentTarget.value === "" &&
              monthInputRef.current
            ) {
              monthInputRef.current.focus();
            }

            if (event.key === "Enter") {
              event.preventDefault();
              handleSave();
            }

            if (event.key === "Escape") {
              cancelEditing();
            }
          }}
        />
      </div>
    ) : (
      renderValue(formatDate(date_birth))
    );

  let actions = null;

  if (!hasDateOfBirth) {
    actions =
      editingField === "date_birth" ? (
        <>
          <button
            type="button"
            onClick={handleSave}
            disabled={
              savingField === "date_birth"
            }
            className="profile-action__save profile-action__button"
          >
            <img src={saveIcon} alt="save" />
          </button>

          <button
            type="button"
            onClick={cancelEditing}
            disabled={savingField === "date_birth"}
            className="profile-action__cancel profile-action__button"
          >
            <img src={cancelIcon} alt="cancel" />
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={handleStartEditing}
          className="profile-action__button profile-action__icon-btn"
        >
          <img src={changeIcon} alt="change" />
        </button>
      );
  }

  return (
    <ProfileRow
      label={__("profile.date_of_birth")}
      value={value}
      actions={actions}
      error={fieldError}
    />
  );
};
